import React, { useState } from 'react';
import { Calendar, Loader2, MapPin, Phone, Video } from 'lucide-react';
import type { InterviewRow } from '../../data/clientEngagementMock';
import { formatNlDateTime, interviewStatusLabel, interviewTypeLabel } from './engagementLabels';

export type PortalInterviewFollowUpKind = 'next_round' | 'reject' | 'reschedule';
import InlineFeedbackNote from './InlineFeedbackNote';

const typeIcon = {
  online: Video,
  locatie: MapPin,
  telefonisch: Phone,
};

const UpcomingInterviewCard: React.FC<{
  interview: InterviewRow;
  noteText: string;
  onAutosaveNote: (id: string, text: string) => void;
  onSendHeadsUp: (id: string, text: string) => void;
  onPortalFollowUp: (inv: InterviewRow, kind: PortalInterviewFollowUpKind, comment: string) => void | Promise<void>;
}> = ({ interview, noteText, onAutosaveNote, onSendHeadsUp, onPortalFollowUp }) => {
  const Icon = typeIcon[interview.type];
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState<PortalInterviewFollowUpKind | null>(null);

  const run = async (kind: PortalInterviewFollowUpKind) => {
    setBusy(kind);
    try {
      await onPortalFollowUp(interview, kind, comment.trim());
      if (kind !== 'reschedule') setComment('');
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{interview.candidateName}</h3>
          <p className="text-sm text-slate-500">{interview.vacancyTitle}</p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200">
          {interviewStatusLabel(interview.status)}
        </span>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-700">
        <Calendar className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        <span className="font-medium">{formatNlDateTime(interview.startsAt)}</span>
        <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {interviewTypeLabel(interview.type)}
        </span>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        {interview.interviewers.length ? `Met: ${interview.interviewers.join(', ')}` : null}
      </p>

      <div className="mt-5 space-y-2">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" htmlFor={`fb-${interview.id}`}>
          Jouw feedback of toelichting
        </label>
        <textarea
          id={`fb-${interview.id}`}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          placeholder="Bijv. indruk na gesprek, voorwaarden, vervolgstap…"
          className="w-full resize-y rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-800 outline-none ring-slate-300/0 transition focus:border-slate-300 focus:ring-2"
        />
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <button
          type="button"
          disabled={!!busy}
          onClick={() => void run('next_round')}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50 sm:min-w-[10rem]"
        >
          {busy === 'next_round' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Volgende ronde
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => void run('reject')}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs font-semibold text-red-900 transition hover:bg-red-100 disabled:opacity-50 sm:min-w-[10rem]"
        >
          {busy === 'reject' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Afwijzen
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => void run('reschedule')}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 sm:min-w-[10rem]"
        >
          {busy === 'reschedule' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Verplaatsing aanvragen
        </button>
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Bericht naar recruitment</p>
        <InlineFeedbackNote
          entityKey={`int-${interview.id}`}
          initialText={noteText}
          onAutosave={(t) => onAutosaveNote(interview.id, t)}
          onSendHeadsUp={(t) => onSendHeadsUp(interview.id, t)}
        />
      </div>
    </article>
  );
};

export default UpcomingInterviewCard;
